/**
 * Administrators and their permissions.
 *
 * Roles are presets over capabilities; an individual record can add or remove
 * single capabilities on top of its role. The server owns the invariants (nobody
 * edits themselves, the last owner stays an owner), so this page only has to
 * avoid offering an action that is guaranteed to fail and to explain the refusal
 * when it does.
 *
 * A record is collapsed by default. The summary carries the address, its role and
 * whether it is enabled, which is what an operator scans for; every control that
 * changes the record lives in the opened part. The capability list is long enough
 * that leaving it visible made each record several screens tall.
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ADMIN_CAPABILITIES, ADMIN_ROLES, type AdminCapability, type AdminRole } from '@shared/constants'
import { type Locale } from '@shared/locales'
import type { AdminDto } from '@shared/types'

import { ErrorAlert, ErrorBlock, LoadingBlock } from '../../components/layout/states'
import { Button } from '../../components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card'
import { Field, FieldGroup } from '../../components/ui/field'
import { Input } from '../../components/ui/input'
import { useAdmins, useCreateAdmin, useUpdateAdmin } from '../../hooks/use-admin'
import { useCurrentUser } from '../../hooks/use-auth'
import { formatUnixSeconds, joinList } from '../../lib/format'
import { currentLocale } from '../../lib/i18n'
import { AdminPage, CollapsibleRow, DetailRow, Pager, StateBadge, adminErrorText, usePageParam } from './shared'

export function AdminAdminsPage() {
  const { t } = useTranslation()
  const locale = currentLocale()
  const { page, goToPage } = usePageParam()
  const { capabilities, user } = useCurrentUser()
  const canManage = capabilities.includes('admin:manage')

  const admins = useAdmins(page)
  const create = useCreateAdmin()

  const [email, setEmail] = useState('')
  const [role, setRole] = useState<AdminRole>('reviewer')

  // Read as text rather than as an `ApiError`: a request that never reached the
  // server still has to say something instead of leaving the form silent.
  const errorText = adminErrorText(create.error, t)

  const pager = admins.data ? (
    <Pager
      page={admins.data.page}
      pageSize={admins.data.pageSize}
      total={admins.data.total}
      onPage={goToPage}
    />
  ) : null

  return (
    <AdminPage
      title={t('admin.admins.title')}
      description={t('admin.admins.description')}
      current={t('admin.nav.admins')}
    >
      {admins.isPending ? <LoadingBlock label={t('common.loading')} /> : null}
      {admins.isError ? (
        <ErrorBlock
          title={t('admin.common.error')}
          retryLabel={t('admin.common.retry')}
          onRetry={() => void admins.refetch()}
        />
      ) : null}

      {pager}

      <div className="flex flex-col gap-2">
        {admins.data?.items.map((admin) => (
          <AdminRow
            key={admin.id}
            admin={admin}
            locale={locale}
            canManage={canManage}
            isSelf={user?.email === admin.email}
          />
        ))}
      </div>

      {pager}

      {canManage ? (
        <Card>
          <CardHeader>
            <CardTitle>{t('admin.admins.add')}</CardTitle>
          </CardHeader>
          <CardContent>
            <form
              className="flex flex-col gap-4"
              onSubmit={(event) => {
                event.preventDefault()
                create.mutate({ email: email.trim(), role }, { onSuccess: () => setEmail('') })
              }}
            >
              <FieldGroup>
                <Field label={t('admin.admins.email')} htmlFor="admin-email" required>
                  <Input
                    id="admin-email"
                    type="email"
                    value={email}
                    autoComplete="off"
                    onChange={(event) => setEmail(event.target.value)}
                  />
                </Field>
              </FieldGroup>

              <div className="flex flex-wrap gap-2">
                {ADMIN_ROLES.map((value) => (
                  <Button
                    key={value}
                    type="button"
                    size="sm"
                    variant={role === value ? 'default' : 'outline'}
                    onClick={() => setRole(value)}
                  >
                    {t(`admin.role.${value}`)}
                  </Button>
                ))}
              </div>

              {errorText ? (
                <ErrorAlert message={errorText} />
              ) : null}

              <Button type="submit" className="self-start" disabled={create.isPending || email.trim().length === 0}>
                {t('admin.admins.submit')}
              </Button>
            </form>
          </CardContent>
        </Card>
      ) : null}
    </AdminPage>
  )
}

interface AdminRowProps {
  admin: AdminDto
  locale: Locale
  canManage: boolean
  isSelf: boolean
}

/**
 * One administrator record.
 *
 * Editing capability exceptions is deliberately explicit: the two lists are
 * labelled as additions and removals so an operator can tell a role's default
 * from a per-person exception.
 */
function AdminRow({ admin, locale, canManage, isSelf }: AdminRowProps) {
  const { t } = useTranslation()
  const update = useUpdateAdmin()
  const [extra, setExtra] = useState<AdminCapability[]>(admin.capabilitiesExtra)
  const [denied, setDenied] = useState<AdminCapability[]>(admin.capabilitiesDenied)

  // Read as text rather than as an `ApiError`: a request that never reached the
  // server still has to say something instead of leaving the row silent.
  const errorText = adminErrorText(update.error, t)

  function toggle(list: AdminCapability[], set: (next: AdminCapability[]) => void, value: AdminCapability) {
    set(list.includes(value) ? list.filter((entry) => entry !== value) : [...list, value])
  }

  return (
    <CollapsibleRow
      title={<span className="truncate text-sm font-medium">{admin.email}</span>}
      meta={
        <span className="text-muted-foreground truncate text-xs">
          {t('admin.admins.updatedAt', { time: formatUnixSeconds(admin.updatedAt, locale) })}
        </span>
      }
      badges={
        <>
          <StateBadge label={t(`admin.role.${admin.role}`)} variant="default" />
          <StateBadge
            label={admin.status === 'active' ? t('admin.admins.active') : t('admin.admins.disabled')}
            variant={admin.status === 'active' ? 'success' : 'secondary'}
          />
        </>
      }
    >
      <DetailRow label={t('admin.admins.effective')}>
        {admin.capabilities.length === 0 ? t('common.empty') : joinList(admin.capabilities, t)}
      </DetailRow>

      {canManage && !isSelf ? (
        <>
          <div className="flex flex-wrap gap-2">
            {ADMIN_ROLES.map((value) => (
              <Button
                key={value}
                type="button"
                size="sm"
                variant={admin.role === value ? 'default' : 'outline'}
                disabled={update.isPending}
                onClick={() => update.mutate({ id: admin.id, role: value })}
              >
                {t(`admin.role.${value}`)}
              </Button>
            ))}
            <Button
              type="button"
              size="sm"
              variant={admin.status === 'active' ? 'destructive' : 'outline'}
              disabled={update.isPending}
              onClick={() =>
                update.mutate({
                  id: admin.id,
                  status: admin.status === 'active' ? 'disabled' : 'active',
                })
              }
            >
              {admin.status === 'active' ? t('admin.admins.disable') : t('admin.admins.enable')}
            </Button>
          </div>

          <details className="text-sm">
            <summary className="cursor-pointer">{t('admin.admins.exceptions')}</summary>
            <div className="flex flex-col gap-2 pt-2">
              {ADMIN_CAPABILITIES.map((capability) => (
                <div key={capability} className="flex flex-wrap items-center gap-3">
                  <span className="text-muted-foreground w-44 text-xs">{capability}</span>
                  <label className="flex items-center gap-1 text-xs">
                    <input
                      type="checkbox"
                      checked={extra.includes(capability)}
                      onChange={() => toggle(extra, setExtra, capability)}
                    />
                    {t('admin.admins.grant')}
                  </label>
                  <label className="flex items-center gap-1 text-xs">
                    <input
                      type="checkbox"
                      checked={denied.includes(capability)}
                      onChange={() => toggle(denied, setDenied, capability)}
                    />
                    {t('admin.admins.deny')}
                  </label>
                </div>
              ))}
              <Button
                size="sm"
                className="self-start"
                disabled={update.isPending}
                onClick={() =>
                  update.mutate({
                    id: admin.id,
                    capabilitiesExtra: extra,
                    capabilitiesDenied: denied,
                  })
                }
              >
                {t('admin.admins.saveExceptions')}
              </Button>
            </div>
          </details>
        </>
      ) : null}

      {isSelf ? (
        <p className="text-muted-foreground text-xs">{t('admin.admins.selfNote')}</p>
      ) : null}

      {errorText ? (
        <ErrorAlert message={errorText} />
      ) : null}
    </CollapsibleRow>
  )
}
